"use client"

import { motion } from "framer-motion"
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from "recharts"
import { Brain, Zap, TrendingUp, Activity, Clock, Target } from "lucide-react"

export default function Dashboard({ insights, activityHistory }) {
  // Mock data for charts
  const brainActivityData = [
    { time: "9:00", activity: 65, focus: 70, stress: 30 },
    { time: "10:00", activity: 75, focus: 80, stress: 25 },
    { time: "11:00", activity: 85, focus: 90, stress: 35 },
    { time: "12:00", activity: 70, focus: 75, stress: 40 },
    { time: "13:00", activity: 60, focus: 65, stress: 45 },
    { time: "14:00", activity: 80, focus: 85, stress: 30 },
    { time: "15:00", activity: 90, focus: 95, stress: 20 },
  ]

  const regionActivityData = [
    { region: "Frontal", activity: 85 },
    { region: "Temporal", activity: 72 },
    { region: "Parietal", activity: 68 },
    { region: "Occipital", activity: 78 },
    { region: "Cerebellum", activity: 65 },
  ]

  const cognitiveRadarData = [
    { subject: "Memory", A: 85, fullMark: 100 },
    { subject: "Attention", A: 78, fullMark: 100 },
    { subject: "Language", A: 92, fullMark: 100 },
    { subject: "Processing", A: 75, fullMark: 100 },
    { subject: "Motor", A: 88, fullMark: 100 },
    { subject: "Emotion", A: 70, fullMark: 100 },
  ]

  const stats = [
    {
      title: "Total Activities",
      value: activityHistory?.length || 0,
      icon: Activity,
      color: "#4361ee",
      trend: "+12%",
    },
    {
      title: "Brain Efficiency",
      value: "87%",
      icon: Brain,
      color: "#f72585",
      trend: "+5%",
    },
    {
      title: "Focus Score",
      value: "92/100",
      icon: Target,
      color: "#4cc9f0",
      trend: "+8%",
    },
    {
      title: "Active Time",
      value: "4.5h",
      icon: Clock,
      color: "#7209b7",
      trend: "+15%",
    },
  ]

  return (
    <div className="dashboard-container">
      <motion.div
        className="dashboard-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="dashboard-title">
          <Brain className="title-icon" />
          Neural Analytics Dashboard
        </h1>
        <p className="dashboard-subtitle">Real-time brain activity monitoring and insights</p>
      </motion.div>

      {/* Stats Cards */}
      <div className="stats-grid">
        {stats.map((stat, index) => {
          const Icon = stat.icon
          return (
            <motion.div
              key={index}
              className="stat-card glass-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.05, y: -5 }}
            >
              <div className="stat-icon" style={{ backgroundColor: `${stat.color}20`, color: stat.color }}>
                <Icon size={24} />
              </div>
              <div className="stat-content">
                <p className="stat-label">{stat.title}</p>
                <h3 className="stat-value">{stat.value}</h3>
                <span className="stat-trend positive">{stat.trend}</span>
              </div>
            </motion.div>
          )
        })}
      </div>

      {/* Charts Grid */}
      <div className="charts-grid">
        {/* Brain Activity Over Time */}
        <motion.div
          className="chart-card glass-card"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="chart-header">
            <h3>
              <TrendingUp size={20} />
              Brain Activity Timeline
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={brainActivityData}>
              <defs>
                <linearGradient id="colorActivity" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4361ee" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#4361ee" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorFocus" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f72585" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#f72585" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="time" stroke="#888" />
              <YAxis stroke="#888" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(0,0,0,0.8)', 
                  border: 'none', 
                  borderRadius: '8px',
                  color: '#fff'
                }} 
              />
              <Area type="monotone" dataKey="activity" stroke="#4361ee" fillOpacity={1} fill="url(#colorActivity)" />
              <Area type="monotone" dataKey="focus" stroke="#f72585" fillOpacity={1} fill="url(#colorFocus)" />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Region Activity */}
        <motion.div
          className="chart-card glass-card"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="chart-header">
            <h3>
              <Brain size={20} />
              Brain Region Activity
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={regionActivityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="region" stroke="#888" />
              <YAxis stroke="#888" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(0,0,0,0.8)', 
                  border: 'none', 
                  borderRadius: '8px',
                  color: '#fff'
                }} 
              />
              <Bar dataKey="activity" fill="#4cc9f0" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Cognitive Performance Radar */}
        <motion.div
          className="chart-card glass-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="chart-header">
            <h3>
              <Zap size={20} />
              Cognitive Performance
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={cognitiveRadarData}>
              <PolarGrid stroke="rgba(255,255,255,0.2)" />
              <PolarAngleAxis dataKey="subject" stroke="#888" />
              <PolarRadiusAxis stroke="#888" />
              <Radar name="Performance" dataKey="A" stroke="#7209b7" fill="#7209b7" fillOpacity={0.6} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(0,0,0,0.8)', 
                  border: 'none', 
                  borderRadius: '8px',
                  color: '#fff'
                }} 
              />
            </RadarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Insights Summary */}
        <motion.div
          className="chart-card glass-card insights-summary"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="chart-header">
            <h3>
              <Activity size={20} />
              Current Insights
            </h3>
          </div>
          {insights ? (
            <div className="insights-content">
              <div className="insight-section">
                <h4>Active Regions</h4>
                <div className="insight-tags">
                  {insights.brain_regions?.slice(0, 4).map((region, idx) => (
                    <span key={idx} className="insight-tag">{region}</span>
                  ))}
                </div>
              </div>
              <div className="insight-section">
                <h4>Cognitive Effects</h4>
                <div className="insight-tags">
                  {insights.cognitive_effects?.slice(0, 3).map((effect, idx) => (
                    <span key={idx} className="insight-tag effect">{effect}</span>
                  ))}
                </div>
              </div>
              <div className="insight-section">
                <h4>Stress Level</h4>
                <div className="stress-indicator">
                  <div className={`stress-badge ${insights.stress_level}`}>
                    {insights.stress_level}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-insights">
              <p>Start analyzing activities to see insights</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  )
}
